/**
 * HistoryList — token transfer history for the active account.
 *
 * Built on the same `fetchPayments` used by the chat: this is that feed without
 * the per-peer filter. Only `transfer` actions — swaps, staking and votes are not
 * shown, see the note in payments.ts.
 */

import { useCallback, useEffect, useState } from 'react'
import { fetchPayments, type ChainPayment } from '@properwallet/chain-eos'
import { useTranslation } from '../hooks/useTranslation'
import AccountChip from './AccountChip'

export default function HistoryList({ account }: { account: string }) {
  const { t } = useTranslation()
  const [items, setItems] = useState<ChainPayment[]>([])
  const [loading, setLoading] = useState(false)
  const [loaded, setLoaded] = useState(false)

  const load = useCallback(async () => {
    if (!account) return
    setLoading(true)
    try {
      setItems(await fetchPayments({ account, limit: 100 }))
    } finally {
      setLoading(false)
      setLoaded(true)
    }
  }, [account])

  useEffect(() => {
    void load()
  }, [load])

  if (loading && !loaded) {
    return (
      <p className="text-sm text-center py-8" style={{ color: '#6b7280' }}>
        {t('common.loading')}
      </p>
    )
  }

  if (items.length === 0) {
    return (
      <p className="text-sm text-center py-8" style={{ color: '#6b7280' }}>
        {t('wallet.noHistory')}
      </p>
    )
  }

  return (
    <div className="space-y-2">
      <div className="flex justify-end">
        <button
          type="button"
          className="btn-ghost"
          style={{ padding: '4px 10px', fontSize: 11, height: 28 }}
          onClick={() => void load()}
          disabled={loading}
        >
          {loading ? '…' : t('common.retry')}
        </button>
      </div>

      {items.map(p => {
        const incoming = p.direction === 'in'
        const peer = incoming ? p.from : p.to
        return (
          <div
            key={`${p.trxId}:${p.from}:${p.to}:${p.quantity}`}
            style={{
              background: '#0c100c',
              border: '1px solid rgba(255,255,255,0.06)',
              borderRadius: 14,
              padding: '10px 14px',
            }}
          >
            <div className="flex justify-between items-baseline gap-2">
              <span
                className="text-sm font-semibold"
                style={{ color: incoming ? '#5cbf63' : '#e5e7eb' }}
              >
                {incoming ? '+' : '−'} {p.quantity}
              </span>
              <span className="text-[10px] flex-shrink-0" style={{ color: '#6b7280' }}>
                {new Date(p.timestamp).toLocaleString()}
              </span>
            </div>
            <p className="text-xs mt-0.5 truncate" style={{ color: '#9ca3af' }}>
              {incoming ? t('wallet.from') : t('wallet.to')}{' '}
              <AccountChip account={peer} style={{ color: '#c8d6c9' }} />
            </p>
            {p.memo && (
              <p className="text-[11px] mt-1 break-words" style={{ color: '#6b7280' }}>
                {p.memo}
              </p>
            )}
          </div>
        )
      })}
    </div>
  )
}
